// pages_config.js — Platform Configuration (SuperAdmin only)
// Manage all dropdown lists: camera brands, types, sectors, incident types, etc.
import React, { useState } from 'react';
import { Btn, Inp, PageHeader, Panel, KPI, getConfig, setConfig, CONFIG_DEFAULTS_EXPORT } from './utils';

const T = {
  accent:"#C48A0E", gold:"#C48A0E", bg:"#F0F2F7", card:"#FFFFFF",
  panel:"#FFFFFF", deep:"#0A1628", text:"#1A2235", muted:"#8492A6",
  border:"#E2E8F0", faint:"rgba(26,34,53,0.04)", blue:"#3B82F6",
  green:"#22C55E", red:"#EF4444", orange:"#F97316", purple:"#7C3AED",
  font:"'DM Sans','Segoe UI',system-ui,sans-serif", mono:"'IBM Plex Mono',monospace",
};

/* ── Single list editor ── */
const ListEditor = ({ title, icon, description, configKey, items, onUpdate }) => {
  const [newItem, setNewItem]   = useState('');
  const [editIdx, setEditIdx]   = useState(null);
  const [editVal, setEditVal]   = useState('');
  const [confirm, setConfirm]   = useState(null);

  const add = () => {
    const v = newItem.trim();
    if (!v || items.includes(v)) return;
    onUpdate(configKey, [...items, v]);
    setNewItem('');
  };

  const remove = (idx) => {
    onUpdate(configKey, items.filter((_, i) => i !== idx));
    setConfirm(null);
  };

  const saveEdit = (idx) => {
    const v = editVal.trim();
    if (!v) return;
    const updated = [...items];
    updated[idx] = v;
    onUpdate(configKey, updated);
    setEditIdx(null); setEditVal('');
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const a = [...items];
    [a[idx-1], a[idx]] = [a[idx], a[idx-1]];
    onUpdate(configKey, a);
  };

  const moveDown = (idx) => {
    if (idx === items.length-1) return;
    const a = [...items];
    [a[idx], a[idx+1]] = [a[idx+1], a[idx]];
    onUpdate(configKey, a);
  };

  return (
    <div style={{background:T.card,borderRadius:12,border:`1px solid ${T.border}`,overflow:'hidden'}}>
      {/* Header */}
      <div style={{padding:'14px 16px',borderBottom:`1px solid ${T.border}`,
        background:'linear-gradient(135deg,rgba(196,138,14,0.06),rgba(196,138,14,0.02))',
        display:'flex',alignItems:'center',gap:10}}>
        <span style={{fontSize:20}}>{icon}</span>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:800,color:T.text}}>{title}</div>
          <div style={{fontSize:10,color:T.muted}}>{description}</div>
        </div>
        <span style={{fontSize:11,fontWeight:700,color:T.gold,background:'rgba(196,138,14,0.1)',
          padding:'3px 8px',borderRadius:10}}>{items.length} items</span>
      </div>

      {/* Items list */}
      <div style={{maxHeight:240,overflowY:'auto'}}>
        {items.map((item, idx) => (
          <div key={idx} style={{display:'flex',alignItems:'center',gap:6,padding:'7px 12px',
            borderBottom:`1px solid ${T.border}`,background:editIdx===idx?'rgba(196,138,14,0.04)':'transparent'}}>

            {/* Order controls */}
            <div style={{display:'flex',flexDirection:'column',gap:1}}>
              <button onClick={()=>moveUp(idx)} disabled={idx===0}
                style={{background:'none',border:'none',cursor:idx===0?'default':'pointer',
                  fontSize:8,color:idx===0?T.border:T.muted,padding:0,lineHeight:1}}>▲</button>
              <button onClick={()=>moveDown(idx)} disabled={idx===items.length-1}
                style={{background:'none',border:'none',cursor:idx===items.length-1?'default':'pointer',
                  fontSize:8,color:idx===items.length-1?T.border:T.muted,padding:0,lineHeight:1}}>▼</button>
            </div>

            <span style={{fontSize:10,color:T.muted,fontFamily:T.mono,width:20,textAlign:'right',flexShrink:0}}>{idx+1}</span>

            {editIdx === idx ? (
              <>
                <input value={editVal} onChange={e=>setEditVal(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter')saveEdit(idx);if(e.key==='Escape'){setEditIdx(null);}}}
                  autoFocus
                  style={{flex:1,padding:'4px 8px',border:`1px solid ${T.gold}`,borderRadius:6,
                    fontSize:12,outline:'none',background:'#fff',color:T.text,fontFamily:T.font}}/>
                <button onClick={()=>saveEdit(idx)}
                  style={{padding:'4px 10px',background:T.green,border:'none',borderRadius:6,
                    color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>✓</button>
                <button onClick={()=>{setEditIdx(null);setEditVal('');}}
                  style={{padding:'4px 8px',background:'none',border:`1px solid ${T.border}`,borderRadius:6,
                    color:T.muted,fontSize:11,cursor:'pointer'}}>✕</button>
              </>
            ) : (
              <>
                <span style={{flex:1,fontSize:12,color:T.text,fontWeight:500}}>{item}</span>
                <button onClick={()=>{setEditIdx(idx);setEditVal(item);}}
                  style={{padding:'3px 8px',background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.2)',
                    borderRadius:5,color:T.blue,fontSize:10,fontWeight:700,cursor:'pointer'}}>Edit</button>
                {confirm===idx ? (
                  <>
                    <button onClick={()=>remove(idx)}
                      style={{padding:'3px 8px',background:T.red,border:'none',borderRadius:5,
                        color:'#fff',fontSize:10,fontWeight:700,cursor:'pointer'}}>Confirm</button>
                    <button onClick={()=>setConfirm(null)}
                      style={{padding:'3px 6px',background:'none',border:`1px solid ${T.border}`,borderRadius:5,
                        color:T.muted,fontSize:10,cursor:'pointer'}}>No</button>
                  </>
                ) : (
                  <button onClick={()=>setConfirm(idx)}
                    style={{padding:'3px 8px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',
                      borderRadius:5,color:T.red,fontSize:10,fontWeight:700,cursor:'pointer'}}>🗑</button>
                )}
              </>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div style={{padding:'20px',textAlign:'center',color:T.muted,fontSize:12}}>
            No items yet — add one below
          </div>
        )}
      </div>

      {/* Add new */}
      <div style={{padding:'10px 12px',borderTop:`1px solid ${T.border}`,
        display:'flex',gap:8,background:'rgba(196,138,14,0.02)'}}>
        <input value={newItem} onChange={e=>setNewItem(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter')add();}}
          placeholder={`Add new ${title.toLowerCase()}...`}
          style={{flex:1,padding:'6px 10px',border:`1px solid ${T.border}`,borderRadius:7,
            fontSize:12,outline:'none',background:'#fff',color:T.text,fontFamily:T.font}}/>
        <button onClick={add} disabled={!newItem.trim() || items.includes(newItem.trim())}
          style={{padding:'6px 14px',background:(!newItem.trim()||items.includes(newItem.trim()))
            ?'rgba(196,138,14,0.3)':`linear-gradient(135deg,${T.gold},#9A6B00)`,
            border:'none',borderRadius:7,color:'#000',fontSize:12,fontWeight:700,cursor:'pointer'}}>
          + Add
        </button>
      </div>
    </div>
  );
};

/* ================================================================
   MAIN PAGE
================================================================ */
export const PagePlatformConfig = ({ toast }) => {
  const [cfg, setCfg] = useState(getConfig);
  const [saved,  setSaved]  = useState(false);

  const handleUpdate = (key, newList) => {
    const updated = setConfig({ [key]: newList });
    setCfg(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset ALL lists to factory defaults? This cannot be undone.')) return;
    localStorage.removeItem('auravex_platform_config');
    const defaults = getConfig();
    setCfg(defaults);
    window.dispatchEvent(new Event('auravex:config_updated'));
    toast('All lists reset to defaults', 'warn');
  };

  const SECTIONS = [
    {
      title: 'Camera Hardware',
      items: [
        { key:'cameraBrands', title:'Camera Brands', icon:'📷', description:'Manufacturers shown in camera registration' },
        { key:'cameraTypes',  title:'Camera Types',  icon:'🎯', description:'Fixed, PTZ, Dome, etc.' },
        { key:'resolutions',  title:'Resolutions',   icon:'📐', description:'720p, 1080p, 4K, etc.' },
        { key:'fpsList',      title:'FPS Options',   icon:'⏱', description:'Frames per second values' },
        { key:'retentions',   title:'Retention Periods', icon:'💾', description:'Recording retention durations' },
      ]
    },
    {
      title: 'Organisation & Sites',
      items: [
        { key:'sectors',   title:'Sectors / Industries', icon:'🏢', description:'Tenant sector types in registration forms' },
        { key:'siteTypes', title:'Site Types', icon:'📍', description:'Types of installation sites' },
      ]
    },
    {
      title: 'Events & Incidents',
      items: [
        { key:'incidentTypes', title:'Incident Types', icon:'🚨', description:'Types of incidents that can be logged' },
        { key:'severity',      title:'Severity Levels', icon:'⚡', description:'Alert and incident severity options' },
      ]
    },
    {
      title: 'Hardware Nodes',
      items: [
        { key:'jetsonsModels', title:'AI Edge Server / Edge Models', icon:'🖥', description:'AI Edge Server hardware models for edge nodes' },
      ]
    },
  ];

  const totalItems = Object.values(cfg).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <PageHeader title="⚙️ Platform Configuration" sub="Manage all dropdown lists and reference data used across the platform — changes apply immediately">
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {saved && (
            <div style={{fontSize:11,color:T.green,fontWeight:700,
              background:'rgba(34,197,94,0.1)',padding:'5px 10px',borderRadius:8}}>
              ✅ Saved
            </div>
          )}
          <Btn v="ghost" sm onClick={resetToDefaults}>↺ Reset Defaults</Btn>
        </div>
      </PageHeader>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
        <KPI label="Total Items"     value={totalItems}                        icon="📋" color={T.blue}/>
        <KPI label="Camera Brands"   value={cfg.cameraBrands?.length||0}       icon="📷" color={T.gold}/>
        <KPI label="Sectors"         value={cfg.sectors?.length||0}            icon="🏢" color={T.purple}/>
        <KPI label="Incident Types"  value={cfg.incidentTypes?.length||0}      icon="🚨" color={T.red}/>
      </div>

      {/* Info banner */}
      <div style={{background:'linear-gradient(135deg,rgba(59,130,246,0.08),rgba(59,130,246,0.03))',
        border:'1px solid rgba(59,130,246,0.2)',borderRadius:10,padding:'12px 16px',
        display:'flex',gap:12,alignItems:'center'}}>
        <span style={{fontSize:20}}>💡</span>
        <div style={{fontSize:12,color:T.text,lineHeight:1.6}}>
          All changes apply <strong>instantly</strong> across the platform — camera registration, incident forms, tenant onboarding and all other dropdowns reflect updates immediately.
          Use <strong>▲▼</strong> to reorder items. The first item in each list becomes the default selection.
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map(section => (
        <div key={section.title}>
          <div style={{fontSize:11,fontWeight:800,color:T.muted,letterSpacing:1.5,
            textTransform:'uppercase',marginBottom:10,paddingLeft:2}}>{section.title}</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:12}}>
            {section.items.map(item => (
              <ListEditor
                key={item.key}
                title={item.title}
                icon={item.icon}
                description={item.description}
                configKey={item.key}
                items={cfg[item.key] || []}
                onUpdate={handleUpdate}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
